import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PermIdentity from '@mui/icons-material/PermIdentity';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  Box,
  Container,
  Snackbar,
  SnackbarCloseReason,
  Tab,
  Tabs,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { sgrColors } from '../../../ui/sgr/SgrColors';
import { StatisticsTab } from './StatisticsTab';
import EntitiesTab from './tabs/EntitiesTab';
import EvidencesTab from './tabs/EvidencesTab';
import MonitoredNamesTab from './tabs/MonitoredNamesTab';
import NamesTab from './tabs/NamesTab';
import TagsTab from './tabs/TagsTab';

const Statistics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab } = useParams();
  const [snackbarError, setSnackbarError] = useState({
    open: false,
    message: '',
  });

  const handleFetchError = useCallback((message: string) => {
    setSnackbarError({ open: true, message });
  }, []);

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarError({
      open: false,
      message: snackbarError.message,
    });
  };

  if (!Object.values(StatisticsTab).includes(tab as StatisticsTab)) {
    return <Navigate to={`../${StatisticsTab.Names}`} replace />;
  }
  const activeTab = tab as StatisticsTab;

  return (
    <>
      <Box
        sx={{
          maxHeight: 'calc(100vh - 12rem)',
          overflowY: 'auto',
          backgroundColor: sgrColors.frost_grey,
        }}
      >
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            paddingY: '1.5rem',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, newValue: StatisticsTab) => navigate(`../${newValue}`)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: '3rem',
              borderBottom: `1px solid ${sgrColors.grey_cloud}`,
              '& .MuiTabs-indicator': {
                height: '3px',
                borderRadius: '3px 3px 0 0',
                backgroundColor: sgrColors.navy,
              },
              '& .MuiTab-root': {
                minHeight: '3rem',
                textTransform: 'none',
                fontSize: '0.9375rem',
                fontWeight: 500,
                color: sgrColors.backdrop,
                '&:hover': { color: sgrColors.navy },
                '&.Mui-selected': { color: sgrColors.navy, fontWeight: 600 },
              },
            }}
          >
            <Tab
              value={StatisticsTab.Names}
              icon={<PersonSearchOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label={t('entity_statistics.names_tab')}
            />
            <Tab
              value={StatisticsTab.Entities}
              icon={<PermIdentity fontSize="small" />}
              iconPosition="start"
              label={t('entity_statistics.entities_tab')}
            />
            <Tab
              value={StatisticsTab.Tags}
              icon={<LocalOfferOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label={t('entity_statistics.tags_tab')}
            />
            <Tab
              value={StatisticsTab.MonitoredNames}
              icon={<VisibilityOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label={t('entity_statistics.monitored_names_tab')}
            />
            <Tab
              value={StatisticsTab.Evidences}
              icon={<FindInPageOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label={t('entity_statistics.evidences_tab')}
            />
          </Tabs>

          {activeTab === StatisticsTab.Names && (
            <NamesTab onFetchError={handleFetchError} />
          )}
          {activeTab === StatisticsTab.MonitoredNames && (
            <MonitoredNamesTab onFetchError={handleFetchError} />
          )}
          {activeTab === StatisticsTab.Tags && (
            <TagsTab onFetchError={handleFetchError} />
          )}
          {activeTab === StatisticsTab.Entities && (
            <EntitiesTab onFetchError={handleFetchError} />
          )}
          {activeTab === StatisticsTab.Evidences && (
            <EvidencesTab onFetchError={handleFetchError} />
          )}
        </Container>
      </Box>
      <Snackbar
        open={snackbarError.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          variant="filled"
          sx={{
            borderRadius: '0.5rem',
            backgroundColor: sgrColors.rose,
            color: '#ffffff',
          }}
        >
          {snackbarError.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Statistics;
